import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { addonSheetToSheet } from '../../../PackageBuilders/MultiPose/functions';
import DisplayableSheet from '../../../PackageBuilders/MultiPose/DisplayableSheet';
import IncrementalButton from '../../../PackageBuilders/IndividualPrintsBuilder/IncrementalButton';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { determineBackground } from './utils';
import ItemSubtitle, { isAnnouncement, priceFor } from './ItemSubtitle';
import { isGrantedSheet } from '../../../../utils/freeOfferAddon';
import Item from './Item';
import relatedProducts from './relatedProducts';
import productKeyOf from './productKey';
import { specLine, detailCopy } from './productCopy';
import StickyBuyBar from './StickyBuyBar';
import PreviewLightbox from './PreviewLightbox';
import { grantsFreeAnnouncements } from '../../../../utils/offerPricing';
import { addonUnitPrice } from '../../../../utils/functions';
import styles from './HolidayBuilder.module.scss';

// How many copies of `addon` an OFFER granted on this package (#5747), from
// the package's own sheets by the marker.
const grantedCountFor = (addon, orderPackage) => (orderPackage?.addonSheets || [])
  .filter((sheet) => String(sheet.addonId) === String(addon?.id) && isGrantedSheet(sheet)).length;

function CartIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="M2 3h2l2 10h9l2-7H5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="8" cy="16.5" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="15" cy="16.5" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

CartIcon.propTypes = { className: PropTypes.string };
CartIcon.defaultProps = { className: '' };

export default function ItemBuilder({
                                      addons,
                                      background,
                                      backgrounds,
                                      isLoading,
                                      orderPackage = null,
                                      initialItems = [],
                                      isSiblingPose = false,
                                      pose,
                                      poses = [],
                                      onSelectPose = () => {},
                                      // Every product this customer may see, for the
                                      // "You might also like" row; `onSelectProduct`
                                      // switches the screen to one of them.
                                      visibleAddons = [],
                                      onSelectProduct = () => {},
                                      selectedAddonType,
                                      offer = null,
                                      qualifiesForFreeAnnouncements = false,
                                      onConfirm,
                                      onCancelProps = {},
                                    }) {
  const [printBuilderCart, setPrintBuilderCart] = useState({});
  const [hasMadeSelection, setHasMadeSelection] = useState(false);

  // EVERY PRODUCT STARTS AT ONE. A shopper on a product screen has already said
  // what they want; starting at zero made them state it again and parked a
  // greyed-out button under the price until they did.
  const MIN_QUANTITY = 1;
  const quantityFor = (addonId) => {
    const chosen = printBuilderCart[addonId];

    return chosen === undefined ? MIN_QUANTITY : chosen;
  };

  const handleDecrement = (addonId) => {
    setPrintBuilderCart((prev) => ({
      ...prev,
      [addonId]: Math.max(quantityFor(addonId) - 1, MIN_QUANTITY),
    }));
    setHasMadeSelection(true);
  };

  const handleIncrement = (addonId) => {
    setPrintBuilderCart((prev) => ({ ...prev, [addonId]: quantityFor(addonId) + 1 }));
    setHasMadeSelection(true);
  };

  const continueText = (addon) => {
    if (addon?.backgroundSelection) {
      return 'Select Backgrounds and Customize'
    }

    return 'Customize'
  }

  // const sheets = addons.map((addon) => addonSheetToSheet(new AddonSheet({ addon, background, pose })));
  const sheets = addons.map((addon) => addonSheetToSheet({ addon, background, pose }));

  const handleConfirm = () => {
    // Reads the DEFAULTED quantity, not the raw map. Personalize is always
    // enabled now, so a customer can press it having touched nothing -- and the
    // map is empty until they do. Off the raw map that would add zero of
    // everything and look like the button had done nothing.
    const addonSheets = addons.map((addon) => (
      new Array(quantityFor(addon.id)).fill(null).map(() => {

        const addonSheet = ({
          addon,
          addonId: addon.id,
          background,
          backgroundId: background.id,
          pose,
          poseId: pose.id,
        });


        const bg = determineBackground(addonSheet, backgrounds);
        return { ...addonSheet, background: bg, backgroundId: bg.id };
      })
    )).flat();

    onConfirm(addonSheets);
  };

  useDeepCompareEffect(() => {
    // Don't clobber in-progress selections: `offer` resolves asynchronously, so
    // once the customer has touched quantities (or we've pre-selected the free
    // bundle), a late offer/addons change must not reset printBuilderCart.
    if (hasMadeSelection) return;

    const nextState = initialItems.reduce((acc, cur) => {
      const count = acc[cur.addonId] || 0;

      return { ...acc, [cur.addonId]: count + 1 };
    }, {});

    // Pre-select the free bundle: when the offer includes a free announcement
    // AND the cart currently unlocks it (senior portraits package > $1),
    // default one announcement to quantity 1 (and enable Continue) so the
    // customer gets their free bundle without having to add it themselves.
    // Without the qualifiesForFreeAnnouncements gate, a customer who lands on
    // /holiday without a package would be pre-selected into a "free" bundle
    // that checkout will then charge for.
    if (Object.keys(nextState).length === 0 && grantsFreeAnnouncements(offer) && qualifiesForFreeAnnouncements) {
      const freeAddon = addons.find(isAnnouncement);
      if (freeAddon) {
        nextState[freeAddon.id] = 1;
        setHasMadeSelection(true);
      }
    }

    setPrintBuilderCart(nextState);
  }, [initialItems, addons, offer]);

  // The product screen, laid out like any storefront's: gallery on the left,
  // everything you need to decide on the right.
  //
  // Deliberately NOT IndividualPrintsBuilder.module.scss any more. This screen
  // borrowed another page's stylesheet, so restyling it meant editing a file the
  // individual-prints builder also renders from — a change here would have
  // silently redesigned that page too.
  //
  // `sheets` is usually one product, but a css_name can carry more than one
  // purchasable row, so the buy box lists them. With one it reads as a plain
  // "Quantity"; with several, each gets its own name, price and stepper.
  const hero = sheets[0];
  const single = sheets.length === 1;
  const heroName = hero?.imageType?.name?.replace('Sibling ', '').trim();

  // Copy comes from productCopy, which drops a value that is only the product's
  // own css_name -- "coffeecup" reached a live product page as a bullet point.
  const heroSpec = specLine(hero?.addon);
  const { lead: detailLead, bullets: detailBullets } = detailCopy(hero?.addon);

  const related = relatedProducts(visibleAddons, hero?.addon);

  // The PRODUCT's own images for the gallery rail -- front, then the reverse
  // where one is uploaded. The API serves exactly these two
  // (`image_url` / `back_image_url`), so the rail appears only for an add-on
  // that has a back, and a single-image product shows no rail at all.
  //
  // ⚠️ NOT the student's poses. This rail used to list those and act as the
  // pose chooser; picking the photo that goes on a product is the item flow's
  // job and is untouched by this -- only the look of this page changed.
  // The PRODUCT's own images for the gallery rail -- VERTICAL ONLY.
  //
  // ⚠️ `backImageUrl` IS THE HORIZONTAL VARIANT, and it is deliberately NOT
  // offered here. Alex, 2026-09-29: "if a product has horizontal and vertical
  // versions of thumbnails, only show the vertical. The horizontal will only be
  // used if, when they are personalizing their item, they choose a horizontal
  // image" -- which is exactly what DisplayableSheet already does, picking
  // `backImageUrl || imageUrl` for a landscape pose. The horizontal is not a
  // second view to browse; it is the same view for a different pose, so putting
  // it in the rail offered a choice that is not the customer's to make.
  //
  // ⚠️ AND ORIENTATION CANNOT BE MEASURED OFF THE FILE -- I tried. Both of Coffee
  // Cup's images measure PORTRAIT; what differs is the shape of the photo SLOT
  // inside the artwork, not the artwork's own aspect. How the app uses the
  // column is the only reliable signal, and that is what this reads.
  //
  // Every add-on currently has exactly one vertical image, so THE RAIL DOES NOT
  // RENDER TODAY. It is kept because the rule is "more than one product image",
  // not "no rail ever": upload a second vertical image for a product and it
  // appears, with no code change.
  const productImages = [hero?.addon?.imageUrl]
    .filter(Boolean)
    .map((url, i, all) => ({ url, label: `Product image ${i + 1} of ${all.length}` }));

  const [productImageIndex, setProductImageIndex] = useState(0);
  // The preview, larger (PreviewLightbox). Pressing the preview box or its
  // "View larger" button opens it; the lightbox draws the SAME `<Item
  // previewOnly>` as the box, so it is the real composite at full size.
  const [previewOpen, setPreviewOpen] = useState(false);

  // A different product may have fewer images than the one just shown.
  useEffect(() => {
    setProductImageIndex(0);
  }, [hero?.addon?.id]);

  // ⚠️ NO HERO, NO PRODUCT SCREEN. `sheets` can legitimately empty out UNDER a
  // customer who is already looking at the product: `hasFreeMagnets` arrives
  // asynchronously from `useCart(..., { refetchOnMount: 'always' })`, and when
  // it flips true `giftAddonVisible` drops `magnet` from the visible list. A
  // customer whose cart already holds the free magnet pair, opening Photo
  // Magnets before that response lands, ends up here with `sheets === []`.
  //
  // Everything below dereferences `hero.addon` bare. THE CUSTOMER APP HAS NO
  // ERROR BOUNDARY (only two admin screens do), so one throw here unmounts the
  // whole SPA -- a white page in the middle of a purchase. Send them back to a
  // shelf that is still correct instead.
  if (!hero?.addon) {
    return (
      <div className="mx-auto max-w-[1240px] px-5 pb-24">
        <nav aria-label="Breadcrumb" className="flex items-center gap-x-2 pb-1 pt-4 text-sm text-gift-inkSoft">
          <button
            type="button"
            onClick={onCancelProps.onClick}
            className={'inline-flex items-center gap-1 border-0 bg-transparent p-0 '
              + 'font-semibold text-gift-accent hover:underline'}
          >
            <span aria-hidden="true">&lsaquo;</span>
            Back to Photo Gifts
          </button>
        </nav>

        <p className="mt-8 text-[16px] text-gift-inkSoft">
          That product is no longer available for this order.
        </p>
      </div>
    );
  }

  return (
    // Opens tight to the top: there is no page heading above a product screen,
    // so the breadcrumb is the first thing on it.
    <div className="mx-auto max-w-[1240px] px-5 pb-24">
      {/* Breadcrumb. The old screen's only way back was a full-width blue
          button UNDER a greyed-out "Customize" -- the loudest control on a
          product page pointed backwards. */}
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-x-2 gap-y-1 pb-1 pt-4 text-sm text-gift-inkSoft">
        <button
          type="button"
          onClick={onCancelProps.onClick}
          className={'inline-flex items-center gap-1 border-0 bg-transparent p-0 '
            + 'font-semibold text-gift-accent hover:underline'}
        >
          <span aria-hidden="true">&lsaquo;</span>
          Back to Photo Gifts
        </button>
        {hero?.addon?.giftCategoryLabel && (
          <>
            <span className="text-gray-300" aria-hidden="true">&rsaquo;</span>
            <span>{hero.addon.giftCategoryLabel}</span>
          </>
        )}
        <span className="text-gray-300" aria-hidden="true">&rsaquo;</span>
        <span className="text-gift-ink">{heroName}</span>
      </nav>

      {/* Gallery then buy box, the buy column capped at 440px as in the
          reference. The gallery column hugs its artwork rather than taking half
          the page: DisplayableSheet renders the mockup at a fixed ~288px (see
          the note on `.flatSheet`), so an even split left it adrift in the
          middle of a much larger empty square. */}
      <div className="mt-1.5 grid grid-cols-1 items-stretch justify-start gap-7 md:grid-cols-[auto_minmax(320px,440px)] md:gap-12">
        {/* ------------------------------------------------ gallery */}
        {/* Product images, then the main preview. A column down the left edge
            of the image on desktop, a row UNDER it on a phone
            (`flex-col-reverse`), which is why the strip comes first in the DOM.

            ⚠️ THESE ARE THE PRODUCT'S OWN IMAGES, NOT THE STUDENT'S (changed
            2026-09-29, Alex: "those should only be there if more than one
            product image is uploaded for the item"). It used to list the
            student's poses and act as the pose CHOOSER; picking the photo that
            goes on a product is handled by the item flow that runs after
            Personalize, so this rail is a product gallery like any other
            storefront's.

            The API serves two product images -- `image_url` and
            `back_image_url` (which holds the HORIZONTAL artwork, not a reverse)
            -- so the rail appears only for an add-on that has both, and never
            for one with a single image. */}
        <div
          className="relative flex flex-col-reverse gap-3 sm:block sm:h-full sm:gap-0"
          /* A test hook: this page takes up to 20s to settle in dev, and a
             browser check needs to tell "no rail because one image" apart from
             "no rail because it has not rendered yet". */
          data-product-images={productImages.length}
        >
          {productImages.length > 1 && (
            <div
              role="group"
              aria-label="Product images"
              /* NO SCROLLING, in either axis. There are at most a handful of
                 product images, so the rail is sized by its content and sits
                 along the top of the main image's left edge -- it no longer has
                 to scroll the way a twenty-pose filmstrip did.
                 ⚠️ `overflow-x: auto` would also turn ON overflow-y (CSS
                 computes a `visible` other-axis to `auto`), which is what drew a
                 scrollbar down the old strip. */
              className={cn(
                'flex gap-2.5',
                'sm:absolute sm:left-0 sm:top-0 sm:w-16 sm:flex-col',
              )}
            >
              {productImages.map((image, i) => (
                <button
                  key={image.url}
                  type="button"
                  onClick={() => setProductImageIndex(i)}
                  aria-label={image.label}
                  aria-pressed={i === productImageIndex}
                  className={cn(
                    'h-16 w-16 shrink-0 overflow-hidden rounded-gift-img border-solid bg-gift-tile p-0',
                    i === productImageIndex
                      ? 'border-2 border-gift-accent'
                      : 'border-2 border-transparent hover:border-gift-line',
                  )}
                >
                  <img
                    src={image.url}
                    alt=""
                    className={cn('h-full w-full object-cover object-top', styles.protectedImage)}
                    draggable="false"
                  />
                </button>
              ))}
            </div>
          )}

          {/* The box's GEOMETRY comes from `flatSheet` — it reproduces the
              container DisplayableSheet was written against; read the note on
              that class before changing its shape. `pdpSheet` adds what this
              screen needs on top: a height taken from the buy panel beside it,
              with width following from the 1:1 ratio. */}
          {/* eslint-disable-next-line max-len */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            onClick={() => setPreviewOpen(true)}
            className={cn(
              styles.flatSheet,
              styles.pdpSheet,
              // `relative` only seats the "View larger" button in the corner;
              // the pose overlays measure against .itemImgWrapper inside Item,
              // not this box, so it moves nothing. `cursor-zoom-in` says the
              // box itself opens the lightbox (Katie, 2026-10-20: tap the
              // preview to zoom, desktop and phone); the button beside it is
              // the keyboard- and screen-reader-reachable way to do the same.
              'relative cursor-zoom-in overflow-hidden rounded-gift-img bg-gift-tile',
              /* ⚠️ CAPPED. `.pdpSheet` is `height: 100%; width: auto` in a
                 stretched row, so the frame takes whatever height the row
                 offers -- with Item as the renderer that came out at 712px, a
                 product image twice the size of the one beside it and taller
                 than the buy panel. The cap holds it near the 360px the screen
                 was designed around without touching `.flatSheet`, whose job is
                 the 1:1 ratio the pose overlays are positioned against. */
              'max-w-[420px] md:max-h-[420px]',
              productImages.length > 1 && 'sm:ml-[78px]',
            )}
          >
            {/* ⚠️ `<DisplayableSheet>` IS A DIRECT CHILD OF THIS BOX, and must
                stay one. Its pose overlays are positioned against the element
                inside the sheet, whose size comes from this box -- see the note
                on `.flatSheet`, which sets the artwork's size nowhere on
                purpose. Wrapping it in even one extra div to centre something
                over it broke the geometry: the Desk Plaque rendered a PORTRAIT
                pose onto its landscape slot, overflowing the plaque. Reported
                2026-09-29, one commit after it was introduced.

                So the alternate product image swaps IN PLACE rather than being
                laid over a hidden preview. It is unreachable today -- the rail
                only offers vertical images and every add-on has one -- and if a
                second vertical image ever makes it reachable, size it here
                WITHOUT wrapping the sheet. */}
            {productImageIndex === 0 ? (
              /* ⚠️ THE SAME RENDERER THE SHELF TILE USES, and that is the point.
                 This drew `<DisplayableSheet sheet={hero} />` while GiftShop's
                 tile drew `<Item previewOnly …>` -- two implementations of "put
                 this customer's pose on this product", and they disagreed. The
                 Desk Plaque was the proof: correct on the shelf, and on the
                 product screen a PORTRAIT pose laid over its landscape slot,
                 overflowing the plaque by 281x338 inside a 360x360 frame.
                 Reported by Alex 2026-09-29 with the shelf tile as the
                 reference for what it should look like.

                 Item owns the per-variant geometry (it nests the pose under
                 `.item .previewSlot`, which is what the overlays are positioned
                 against); DisplayableSheet is the cart/package-builder path and
                 was never taught this page's variants. One renderer now, so the
                 two cannot drift again. */
              <Item
                previewOnly
                addon={hero.addon}
                variant={hero.addon.cssName}
                background={background}
                pose={pose}
              />
            ) : (
              <img
                src={productImages[productImageIndex].url}
                alt={productImages[productImageIndex].label}
                className={cn('max-h-full max-w-full object-contain', styles.protectedImage)}
                draggable="false"
              />
            )}

            {/* A SIBLING of the preview, never a wrapper around it (see the
                note above on why the preview must stay this box's direct
                child). Stops the click so the box's own handler does not
                fire twice. */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPreviewOpen(true); }}
              aria-label="View larger"
              className={'absolute bottom-2 right-2 flex h-11 w-11 items-center justify-center rounded-gift-pill '
                + 'border-0 bg-white/85 text-gift-ink shadow-sm hover:bg-white '
                + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-gift-accent'}
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M3 8V3h5M17 12v5h-5M3 3l5.5 5.5M17 17l-5.5-5.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <PreviewLightbox
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            label={`${heroName}, larger`}
          >
            {productImageIndex === 0 ? (
              <Item
                previewOnly
                addon={hero.addon}
                variant={hero.addon.cssName}
                background={background}
                pose={pose}
              />
            ) : (
              <img
                src={productImages[productImageIndex].url}
                alt={productImages[productImageIndex].label}
                className={cn('max-h-full max-w-full object-contain', styles.protectedImage)}
                draggable="false"
              />
            )}
          </PreviewLightbox>
        </div>

        {/* ------------------------------------------------ buy box */}
        <div>
          <h1 className="text-2xl font-extrabold leading-tight tracking-tight text-gift-ink md:text-[28px]">
            {heroName}
          </h1>

          {heroSpec && (
            <p className="mt-1.5 text-[15px] text-gift-inkSoft">{heroSpec}</p>
          )}

          {/* ⚠️ GREY AND UNBOLDED (Alex, 2026-09-29: "make the product price a
              grey, and the product name black"). The price was `font-extrabold
              text-gift-ink`, the same weight AND the same colour as the product
              name directly above it, so the two competed and the name stopped
              reading as the heading. The name keeps the page's ink; the price
              steps back to `gift-inkSoft`.

              ⚠️ WEIGHT AND COLOUR LIVE HERE, NOT IN ItemSubtitle. That component
              renders a <div> with no weight or colour of its own precisely so
              each caller sets them -- hardcoding them there once kept the
              shelf's prices bold when the shelf went regular. */}
          <div className="mt-2 text-[22px] font-normal text-gift-inkSoft">
            <ItemSubtitle
              price={addonUnitPrice(hero.addon, orderPackage)}
              addon={hero.addon}
              offer={offer}
              qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
              /* An offer put a copy of THIS product on the package (#5747):
                 struck through, Free, and the note that extra copies are
                 paid. Read off the package's real sheets, by the marker.
                 Only this heading line -- the buy bar below quotes the price
                 of another copy and must keep the paid price. */
              grantedCount={grantedCountFor(hero.addon, orderPackage)}
            />
          </div>

          {/* QUANTITY AND THE BUTTON COME BEFORE THE COPY, as they do on the
              Shopify store this follows. A shopper who already knows what they
              want should not have to read past the description to buy, and the
              description is what they scroll to if they are still deciding. */}
          <div className="mt-5">
            {sheets.map((sheet) => (
              <div key={sheet.addon.id} className={cn(!single && 'mb-4 border-b border-gray-100 pb-4 last:border-0')}>
                {!single && (
                  <div className="mb-1.5 flex items-baseline justify-between gap-3">
                    <span className="text-sm font-semibold text-gray-900">
                      {sheet.imageType.name.replace('Sibling ', '').trim()}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      <ItemSubtitle
                        price={addonUnitPrice(sheet.addon, orderPackage)}
                        addon={sheet.addon}
                        offer={offer}
                        qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
                      />
                    </span>
                  </div>
                )}

                {/* Label BESIDE the stepper, not above it — the reference's
                    `.qty-row`. `items-center` keeps the two on one baseline
                    however tall the stepper renders. */}
                <div className="flex flex-wrap items-center gap-3.5">
                  {single && (
                    <span className="text-[15px] font-bold text-gift-ink">Quantity</span>
                  )}

                  {/* IncrementalButton fills its container, so it needs one --
                      unbounded it stretched the full width of the buy box. */}
                  <div className={cn(styles.qtyStepper, 'w-[11rem]')}>
                    <IncrementalButton
                      initialCount={quantityFor(sheet.addon.id)}
                      minValue={MIN_QUANTITY}
                      onDecrement={() => handleDecrement(sheet.addon.id)}
                      onIncrement={() => handleIncrement(sheet.addon.id)}
                      maxValue={sheet.addon.quantity && 1}
                      quantity={sheet.addon.quantity}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* The primary action, and the only prominent one on the screen: the
              reference's full-width pill. The label stays `continueText`'s
              rather than the reference's flat "Personalize" because it names
              what actually happens next -- background selection, for products
              that have it. */}
          {/* NEVER DISABLED. Quantity starts at 1, so there is no state in which
              pressing this is meaningless -- the greyed-out button under the
              price was only ever waiting for a number the customer had already
              implied by opening the product.

              Hidden on phones, where the sticky bar below carries it instead. */}
          <button
            type="button"
            disabled={isLoading}
            onClick={handleConfirm}
            className={cn(
              'mt-3.5 flex w-full items-center justify-center gap-2.5 rounded-gift-pill border-0 px-6 py-3.5',
              'text-base font-extrabold text-white transition',
              // `max-sm:hidden`, NOT `sr-only sm:not-sr-only`: not-sr-only resets
              // width AND padding to auto/0, which collapsed this pill to a chip.
              // And not bare `hidden` + `sm:flex` either -- Bootstrap's
              // `.hidden` is !important and could never be un-hidden. This class
              // only ever hides, so nothing has to win it back.
              'max-sm:hidden',
              isLoading
                ? 'cursor-wait bg-gray-300'
                : 'bg-gift-accent shadow-[0_6px_16px_rgba(28,110,158,.32)] hover:bg-gift-accentHover',
            )}
          >
            <CartIcon className="h-5 w-5" />
            {isLoading ? 'Adding…' : 'Personalize'}
          </button>

          {(detailLead || detailBullets.length > 0) && (
            <details className={cn(styles.detailsAcc, 'mt-4')} open>
              <summary className="flex cursor-pointer items-center justify-between py-3 text-[15px] font-bold text-gift-ink">
                Details
              </summary>

              <div className="pb-3.5">
                {detailLead && (
                  <p className="text-sm text-gift-inkSoft">{detailLead}</p>
                )}

                {detailBullets.length > 0 && (
                  /* ⚠️ DIVS, NOT <ul>/<li>. A global stylesheet styles bare
                     lists hard enough that Tailwind cannot win: as a list these
                     laid out side by side, and once forced to a column each item
                     still resolved to 135px inside a 540px container and wrapped
                     mid-phrase. A list role keeps the semantics. */
                  <div role="list" className={cn('flex flex-col gap-1.5', detailLead && 'mt-2.5')}>
                    {detailBullets.map((line) => (
                      <div role="listitem" key={line} className="flex gap-2 text-sm text-gift-inkSoft">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-gift-pill bg-gray-400" aria-hidden="true" />
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </details>
          )}

        </div>
      </div>

      {/* ------------------------------------------------ you might also like */}
      {/* Same category first, topped up from the rest of the catalogue — see
          relatedProducts for why this is not a "most popular" row. Rendered only
          when there is something to offer: a school with one gift on its
          pricelist gets no empty shelf. */}
      {related.length > 0 && (
        /* ⚠️ DELIBERATELY SMALLER THAN THE PRODUCT ABOVE IT (Alex, 2026-09-29:
           "it is competing with the main product"). At four across it drew
           tiles about 290px wide on a 1240px page -- LARGER than the product
           the customer opened, which sits in a ~370px box -- so the secondary
           row read as the main event. Six across puts them near 185px, and the
           heading drops from 22px to 17px so it reads as a shelf label rather
           than a second page title. */
        <section className="mt-12">
          <h2 className="mb-3 text-[17px] font-bold tracking-tight text-gift-ink">
            You might also like
          </h2>

          {/* Six across, three on a phone -- one more per row than the catalog
              grid at every width, because these are suggestions, not the shelf. */}
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 sm:gap-4 lg:grid-cols-6 lg:gap-5">
            {related.map((addon) => (
              <Item
                addon={addon}
                key={addon.name}
                titleText={addon.name}
                subtitleText={(
                  <ItemSubtitle
                    price={addonUnitPrice(addon, orderPackage)}
                    addon={addon}
                    offer={offer}
                    qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
                  />
                )}
                variant={addon.cssName}
                onClick={() => onSelectProduct(productKeyOf(addon))}
                background={background}
                pose={pose}
              />
            ))}
          </div>
        </section>
      )}

      {/* Phones only: the in-panel CTA is hidden there and this carries it. */}
      <StickyBuyBar
        productName={heroName}
        /* THE PRICE THE PANEL SHOWS, not the list price -- on a phone this bar
           is the only one of the two in view, and it is the control that gets
           pressed. See the note on `priceFor`. */
        unitPrice={priceFor(addonUnitPrice(hero.addon, orderPackage), {
          addon: hero.addon, offer, qualifiesForFreeAnnouncements,
        }).value}
        quantity={quantityFor(hero.addon.id)}
        isLoading={isLoading}
        onPersonalize={handleConfirm}
      />
    </div>
  );
}
